// openEHR RM model — declarations with no BMM package in the map
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { ARCHETYPED, FEEDER_AUDIT, LINK } from "./common/archetyped.ts";
import type { PARTY_PROXY } from "./common/generic.ts";
import type { EVENT_CONTEXT } from "./composition/composition.ts";
import type { CODE_PHRASE, DV_CODED_TEXT, DV_TEXT, TERM_MAPPING } from "./data_types/text.ts";
import type { DV_URI } from "./data_types/uri.ts";
import * as openehr_base from "../base/mod.ts";

/**
 * A text item, which may contain any amount of legal characters arranged as e.g. words, sentences etc (i.e. one \`DV_TEXT\` may be more than one word). Visual formatting and hyperlinks may be included via markdown.
 *
 * If the \`_formatting_\` field is set, the \`_value_\` field is affected as follows:
 *
 * * \`_formatting_ = "plain"\`: plain text, may contain newlines;
 * * \`_formatting_ = "plain_no_newlines"\`: plain text with no newlines;
 * * \`_formatting_ = "markdown"\`: text in markdown format; use of CommonMark strongly recommended.
 *
 * A \`DV_TEXT\` can be coded by adding mappings to it.
 */
/**
 * Initialization type for DV_TEXT.
 */
export type DvTextInit = {
  value?: string;
  hyperlink?: DV_URI | Partial<DV_URI>;
  formatting?: string;
  mappings?: TERM_MAPPING[] | Partial<TERM_MAPPING>[];
  language?: string | CODE_PHRASE | Partial<CODE_PHRASE>;
  encoding?: string | CODE_PHRASE | Partial<CODE_PHRASE>;
};

/**
 * A text item whose value must be the rubric from a controlled terminology, the key (i.e. the 'code') of which is the \`_defining_code_\` attribute. In other words: a \`DV_CODED_TEXT\` is a combination of a \`CODE_PHRASE\` (effectively a code) and the rubric of that term, from a terminology service, in the language in which the data were authored.
 *
 * Since \`DV_CODED_TEXT\` is a subtype of \`DV_TEXT\`, it can be used in place of it, effectively allowing the type \`DV_TEXT\` to mean  a text item, which may optionally be coded.
 *
 * Misuse: If the intention is to represent a term code attached in some way to a fragment of plain text, \`DV_CODED_TEXT\` should not be used; instead use a \`DV_TEXT\` and a \`TERM_MAPPING\` to a \`CODE_PHRASE\`.
 */
/**
 * Initialization type for DV_CODED_TEXT.
 */
export type DvCodedTextInit = DvTextInit & {
  defining_code?: string | CODE_PHRASE | Partial<CODE_PHRASE>;
};

/**
 * A fully coordinated (i.e. all coordination has been performed) term from a terminology service (as distinct from a particular terminology).
 */
/**
 * Initialization type for CODE_PHRASE.
 */
export type CodePhraseInit = {
  terminology_id?: string | openehr_base.TERMINOLOGY_ID | Partial<openehr_base.TERMINOLOGY_ID>;
  code_string?: string;
  preferred_term?: string;
};

/**
 * Content of one version in a \`VERSIONED_COMPOSITION\`. A Composition is considered the unit of modification of the record, the unit of transmission in record Extracts, and the unit of attestation by authorising clinicians. In this latter sense, it may be considered equivalent to a signed document.
 *
 * NOTE: It is strongly recommended that the inherited attribute \`_uid_\` be populated in Compositions, using the UID copied from the \`_object_id()_\` of the \`_uid_\` field of the enclosing \`VERSION\` object. +
 * For example, the \`ORIGINAL_VERSION.uid\` \`87284370-2D4B-4e3d-A3F3-F303D2F4F34B::uk.nhs.ehr1::2\` would be copied to the \`_uid_\` field of the Composition.
 */
/**
 * Initialization type for COMPOSITION.
 * Supports multiple input formats for convenience.
 */
export type CompositionInit = {
  // Inherited from LOCATABLE
  archetype_node_id?: string;
  name?: string | DV_TEXT | Partial<DV_TEXT>;
  uid?: string | openehr_base.OBJECT_VERSION_ID | Partial<openehr_base.OBJECT_VERSION_ID>;
  archetype_details?: ARCHETYPED | Partial<ARCHETYPED>;
  feeder_audit?: FEEDER_AUDIT | Partial<FEEDER_AUDIT>;
  links?: LINK[] | Partial<LINK>[];
  
  // COMPOSITION-specific properties
  language?: string | CODE_PHRASE | Partial<CODE_PHRASE>;
  territory?: string | CODE_PHRASE | Partial<CODE_PHRASE>;
  category?: string | DV_CODED_TEXT | Partial<DV_CODED_TEXT>;
  context?: EVENT_CONTEXT | Partial<EVENT_CONTEXT>;
  composer?: PARTY_PROXY | Partial<PARTY_PROXY>;
  content?: any; // CONTENT_ITEM[] but type not fully defined yet
};
